import { defineStore } from 'pinia';
import { Metric } from 'web-vitals';
import { Router } from '#vue-router';

export const PUNCHOUT_SESSION_STORAGE_KEY = 'punchout-settings';
const TOAST_TIMEOUT = 3000;

export const MODES = {
  PUNCHOUT: 'PUNCHOUT',
  APP: 'APP',
  NORMAL: 'NORMAL',
};

export type OverlayComponentKey =
  | 'stickyHeader'
  | 'productMenu'
  | 'servicesMenu'
  | 'faytContainer'
  | 'historyContainer'
  | 'mobileNav'
  | 'orderRequestFlyout'
  | 'priceFileFlyout'
  | 'accountFlyout'
  | 'projectFlyout'
  | 'cartFlyout'
  | 'extraMenu'
  | 'favoritesFlyout'
  | 'zipCodeFlyout'
  | 'deliveryMethodsInfoFlyout'
  | 'storePackagedInfoModal';

export type OverlayComponents = {
  [key in OverlayComponentKey]?: boolean;
};

export const useUiStore = defineStore('ui', () => {
  const config = useRuntimeConfig();
  const message = ref<{ text?: string; severity?: string }>({});
  const toastMessages = ref<string[]>([]);
  const marketingBanners = ref({});
  const mode = ref(MODES.NORMAL);
  const settings = ref<{ punchout: { companyName?: string; callback_url?: string } }>({
    punchout: {
      companyName: '',
      callback_url: '',
    },
  });
  const overlayComponents = ref<OverlayComponents>({});
  const webVitals = ref<Metric[]>([]);
  const showFeaturesInDevelopment = config?.public?.enableFeaturesInDevelopment;

  const setMarketingBanners = (banners: object): void => {
    marketingBanners.value = banners;
  };

  const clearModeState = (): void => {
    mode.value = MODES.NORMAL;
    settings.value.punchout = {};
  };

  const clearOverlayComponents = (): void => {
    overlayComponents.value = {};
  };

  const setOverlayComponents = ({
    components,
    clear = true,
  }: {
    components: OverlayComponents;
    clear?: boolean;
  }): void => {
    const initialValue = clear ? {} : overlayComponents.value;
    const updated = { ...initialValue, ...components };
    overlayComponents.value = updated;
  };

  const addWebVital = (vital: Metric): void => {
    webVitals.value.push(vital);
  };

  const clearWebVitals = (): void => {
    webVitals.value = [];
  };

  const setStartMessage = (payload: { text: string; severity?: string }) => {
    const { $i18n } = useNuxtApp();
    const { text, severity } = payload || { text: '', severity: 'info' };
    sessionStorage.setItem('startMessage', JSON.stringify({ text: $i18n.te(text) ? $i18n.t(text) : text, severity }));
  };

  const displayStartMessage = (): void => {
    const startMessage = sessionStorage.getItem('startMessage');
    if (startMessage) {
      const { text, severity } = JSON.parse(startMessage);
      message.value = { text, severity };
      sessionStorage.removeItem('startMessage');
    }
  };

  const showMessage = (payload: { text: string; severity?: string }): void => {
    const { $i18n } = useNuxtApp();
    const { text, severity } = payload || { text: '', severity: 'info' };
    const hasTranslation = $i18n.te(text);
    message.value = { text: hasTranslation ? $i18n.t(text) : text, severity };
  };

  const hideMessage = (): void => {
    message.value = {};
  };

  const addToastMessage = ({ text, timeout }: { text: string; timeout?: number }): void => {
    toastMessages.value = [...toastMessages.value, text];

    setTimeout(() => {
      const currentToastMessages = [...toastMessages.value];
      currentToastMessages.splice(toastMessages.value.indexOf(text), 1);
      toastMessages.value = currentToastMessages;
    }, timeout || TOAST_TIMEOUT);
  };

  const toggleProductMenu = (): void => {
    setOverlayComponents({
      components: {
        servicesMenu: false,
        extraMenu: false,
        productMenu: !overlayComponents.value.productMenu,
      },
    });
  };

  const toggleExtraMenu = (): void => {
    setOverlayComponents({
      components: {
        servicesMenu: false,
        productMenu: false,
        extraMenu: !overlayComponents.value.extraMenu,
      },
    });
  };

  const setIsMobileNavExpanded = (payload: boolean): void => {
    setOverlayComponents({ components: { mobileNav: payload } });
  };

  const toggleCartFlyout = (): void => {
    setOverlayComponents({ components: { cartFlyout: !overlayComponents.value.cartFlyout } });
  };

  const toggleProjectFlyout = (): void => {
    setOverlayComponents({ components: { projectFlyout: !overlayComponents.value.projectFlyout } });
  };

  const toggleFaytContainer = (payload: boolean): void => {
    const historyContainer = payload === true ? false : overlayComponents.value.historyContainer;
    setOverlayComponents({
      clear: false,
      components: {
        faytContainer: payload,
        historyContainer,
      },
    });
  };

  const toggleHistoryContainer = (payload: boolean): void => {
    setOverlayComponents({ components: { historyContainer: payload } });
  };

  const toggleServicesMenu = (): void => {
    setOverlayComponents({
      components: {
        productMenu: false,
        extraMenu: false,
        servicesMenu: !overlayComponents.value.servicesMenu,
      },
    });
  };

  const toggleAccountFlyout = (): void => {
    setOverlayComponents({ components: { accountFlyout: !overlayComponents.value.accountFlyout } });
  };

  const toggleFavoritesFlyout = (): void => {
    setOverlayComponents({ components: { favoritesFlyout: !overlayComponents.value.favoritesFlyout } });
  };

  const setMode = ({
    mode: m = MODES.NORMAL,
    options = {},
  }: {
    mode: string;
    options?: {
      router?: Router;
      query?: Record<string, string>;
      settings?: { punchout: { companyName?: string; callback_url?: string } };
    };
  }) => {
    const { $i18n } = useNuxtApp();
    mode.value = m;

    if (m === MODES.PUNCHOUT) {
      const {
        router,
        query,
        settings: settingsOption,
      } = options as {
        router: Router;
        query: Record<string, string>;
        settings: { punchout: { companyName?: string; callback_url?: string } };
      };

      const newSettings = createPunchoutSettings({
        punchout: {
          callback_url: query.HOOK_URL ?? (settingsOption?.punchout?.callback_url as string),
          companyName: query.Bolag ?? (settingsOption?.punchout?.companyName as string),
        },
        HOOK_URL: query.HOOK_URL,
        Bolag: query.Bolag,
        email: query.email,
        logonId: query.logonId,
        oci: query.oci,
      });

      localStorage.setItem(PUNCHOUT_SESSION_STORAGE_KEY, JSON.stringify(newSettings));
      if (!newSettings.HOOK_URL) {
        if (settingsOption?.punchout?.callback_url && settingsOption?.punchout?.companyName) {
          newSettings.HOOK_URL = settingsOption?.punchout?.callback_url;
          newSettings.Bolag = settingsOption?.punchout?.companyName;
        } else {
          mode.value = MODES.NORMAL;
          showMessage({ text: $i18n.t('errors.punchout.missing_callback_url') });
          return;
        }
      }

      settings.value = {
        ...settings.value,
        punchout: {
          companyName: newSettings.Bolag,
          callback_url: newSettings.HOOK_URL,
        },
      };

      router?.replace({ query: {} });
    }
  };

  const isPunchout = computed<boolean>(() => mode.value === MODES.PUNCHOUT);

  const messageText = computed<string>(() => message.value.text || '');

  const messageSeverity = computed<string>(() => message.value.severity || 'info');

  return {
    mode,
    webVitals,
    toastMessages,
    marketingBanners,
    addToastMessage,
    showMessage,
    clearModeState,
    overlayComponents,
    clearOverlayComponents,
    setOverlayComponents,
    setMarketingBanners,
    addWebVital,
    clearWebVitals,
    setStartMessage,
    displayStartMessage,
    hideMessage,
    toggleProductMenu,
    setIsMobileNavExpanded,
    toggleCartFlyout,
    toggleProjectFlyout,
    toggleFaytContainer,
    toggleHistoryContainer,
    toggleServicesMenu,
    toggleExtraMenu,
    toggleAccountFlyout,
    toggleFavoritesFlyout,
    setMode,
    isPunchout,
    showFeaturesInDevelopment,
    messageText,
    messageSeverity,
    settings,
  };
});
